import { StatsigClient } from '@statsig/js-client';

export const GEOCOMPLY_FEATURE_GATE_NAME = 'web_geocomply';
export const GEOCOMPLY_UI_GATE_NAME = 'web_geocomply_ui';

/**
 * Imperative master-gate read for the GeoComply shadow rollout.
 *
 * Missing or not-yet-initialized Statsig state intentionally defaults off so
 * the integration remains inert and trading stays fail-open.
 */
export function isGeoComplyFeatureEnabled(): boolean {
    return StatsigClient.instance()?.checkGate(GEOCOMPLY_FEATURE_GATE_NAME) ?? false;
}

/**
 * Enables user-visible intervention and the matching fail-closed request guard.
 *
 * Requiring the master gate as well prevents the UI gate from activating
 * GeoComply by itself while the shadow rollout is incomplete.
 */
export function isGeoComplyUiEnabled(): boolean {
    const client = StatsigClient.instance();
    if (!client?.checkGate(GEOCOMPLY_FEATURE_GATE_NAME)) return false;

    return client.checkGate(GEOCOMPLY_UI_GATE_NAME);
}

export type GeoComplyEnforcementReading = 'enforce' | 'off' | 'unavailable';

/**
 * Reasons whose gate value reflects a real evaluation of loaded config for
 * this user. Everything else — `Uninitialized`, `NoValues`, `Loading`, or a
 * payload that doesn't contain the gate (`…:Unrecognized`) — reports the
 * SDK's false default, not a decision anyone made.
 */
const EVALUATED_GATE_REASON = /^(Network|NetworkNotModified|Cache|Bootstrap|Prefetch).*:Recognized$/;

function readEvaluatedGate(client: StatsigClient, gateName: string): boolean | null {
    const gate = client.getFeatureGate(gateName);
    return EVALUATED_GATE_REASON.test(gate.details.reason) ? gate.value : null;
}

/**
 * Enforcement reading for geo-restricted trades, distinguishing a deliberate
 * gate-off ('off') from Statsig being unable to evaluate ('unavailable'):
 * no client, config never fetched (content blockers commonly block the
 * Statsig endpoints), still initializing, or gates missing from the payload.
 *
 * Trade guards must fail closed on 'unavailable' — a blocked config fetch
 * must not silently disable compliance enforcement the way the boolean
 * `isGeoComplyUiEnabled` read (false by default) would.
 */
export function readGeoComplyEnforcement(): GeoComplyEnforcementReading {
    const client = StatsigClient.instance();
    if (!client) return 'unavailable';

    // A recognized-false master gate is the kill switch and wins outright —
    // it must not be overridden into 'unavailable' by a UI gate that can't
    // be evaluated (e.g. removed from the project).
    const masterGate = readEvaluatedGate(client, GEOCOMPLY_FEATURE_GATE_NAME);
    if (masterGate === false) return 'off';
    if (masterGate == null) return 'unavailable';

    const uiGate = readEvaluatedGate(client, GEOCOMPLY_UI_GATE_NAME);
    if (uiGate == null) return 'unavailable';
    return uiGate ? 'enforce' : 'off';
}
